import React, { useState, useEffect } from "react";
import {
  Offcanvas,
  Form,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import ReactSelect from "react-select";
import { Icon } from "../../../utils/iconHelper";
import { useEntityLabels } from "../../../_context/EntityLabelsContext";
import CourseManagementService, {
  CourseDetail,
  CourseFormData,
  CourseLookups,
} from "../../../_services/CourseManagementService";
import "../course-form-panel.css";

interface Props {
  show: boolean;
  onHide: () => void;
  courseId: number | null; // null = create mode
  lookups: CourseLookups;
  onSuccess: () => void;
  viewOnly?: boolean;
}

const emptyForm: CourseFormData = {
  name: "",
  courseShortCode: "",
  courseType: null,
  streamId: null,
  isElective: null,
  enableStatus: 1,
  listPrice: null,
  numberOfSessions: null,
  remarks: null,
  startDate: null,
  endDate: null,
  maxStudents: null,
  lectures: null,
  tutorials: null,
  practicals: null,
  internalCredit: null,
  externalCredit: null,
  localFee: null,
  internationalFee: null,
  sortOrder: null,
  schoolIds: [],
  termIds: [],
};

/** Card-style section wrapper with a coloured icon header. */
const SectionCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  accent: string; // base hue used for icon bg/fg
  extra?: React.ReactNode;
  children: React.ReactNode;
}> = ({ icon, title, accent, extra, children }) => (
  <div className="cfp-section">
    <div className="cfp-section-head">
      <span
        className="cfp-section-icon"
        style={{ background: `${accent}1a`, color: accent }}
      >
        {icon}
      </span>
      <span className="cfp-section-title">{title}</span>
      {extra && <span className="cfp-section-extra">{extra}</span>}
    </div>
    {children}
  </div>
);

const CourseFormPanel: React.FC<Props> = ({ show, onHide, courseId, lookups, onSuccess, viewOnly = false }) => {
  const { label, labelPlural } = useEntityLabels();
  const [formData, setFormData] = useState<CourseFormData>({ ...emptyForm });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEditMode = courseId !== null;
  const entityName = label("course", "Course").replace(/\s*Management\s*$/i, "") || "Course";

  useEffect(() => {
    if (!show) return;
    if (courseId) {
      loadCourse(courseId);
    } else {
      setFormData({ ...emptyForm });
      setError(null);
    }
  }, [courseId, show]);

  const loadCourse = async (id: number) => {
    setFetching(true);
    setError(null);
    const res = await CourseManagementService.getCourseById(id);
    if (res.success && res.data) {
      const c = res.data;
      const termIds = c.termIds || [];
      setFormData({
        name: c.name || "",
        courseShortCode: c.courseShortCode || "",
        courseType: c.courseType,
        streamId: c.streamId,
        isElective: c.isElective,
        enableStatus: c.enableStatus,
        listPrice: c.listPrice,
        numberOfSessions: c.numberOfSessions,
        remarks: c.remarks,
        startDate: c.startDate,
        endDate: c.endDate,
        maxStudents: c.maxStudents,
        lectures: c.lectures,
        tutorials: c.tutorials,
        practicals: c.practicals,
        internalCredit: c.internalCredit,
        externalCredit: c.externalCredit,
        localFee: c.localFee,
        internationalFee: c.internationalFee,
        sortOrder: c.sortOrder,
        schoolIds: c.schoolIds || [],
        termIds,
      });
    } else {
      setError(res.message || "Failed to load course details");
    }
    setFetching(false);
  };

  const handleChange = (field: keyof CourseFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleNumberChange = (field: keyof CourseFormData, raw: string) => {
    const val = raw === "" ? null : Number(raw);
    handleChange(field, val);
  };

  const toggleSchool = (schoolId: number) => {
    setFormData((prev) => {
      const ids = prev.schoolIds.includes(schoolId)
        ? prev.schoolIds.filter((id) => id !== schoolId)
        : [...prev.schoolIds, schoolId];
      return { ...prev, schoolIds: ids };
    });
  };

  const allSchoolsSelected =
    lookups.schools.length > 0 &&
    lookups.schools.every((s) => formData.schoolIds.includes(s.id));

  const toggleAllSchools = () => {
    setFormData((prev) => ({
      ...prev,
      schoolIds: allSchoolsSelected ? [] : lookups.schools.map((s) => s.id),
    }));
  };

  // Program name lookup, used to label terms by their program.
  const programNameById = new Map<number, string>(
    lookups.programs.map((p) => [p.id, p.name])
  );
  const programLabel = (programId: number | null) =>
    programId != null ? programNameById.get(programId) || `Program #${programId}` : "(Unassigned)";

  // Terms grouped by program → react-select grouped options.
  // Lets a course map term combinations spanning multiple programs at once.
  const termGroupedOptions = (() => {
    if (!lookups.isProgramBased) return [] as any[];
    const byProgram = new Map<number | null, { value: number; label: string }[]>();
    lookups.terms.forEach((t) => {
      const key = t.programId ?? null;
      if (!byProgram.has(key)) byProgram.set(key, []);
      byProgram.get(key)!.push({ value: t.id, label: t.name });
    });
    return Array.from(byProgram.entries()).map(([programId, options]) => ({
      label: programLabel(programId),
      options,
    }));
  })();

  // Currently selected terms as react-select values (labelled with their program).
  const selectedTermOptions = lookups.terms
    .filter((t) => formData.termIds.includes(t.id))
    .map((t) => ({ value: t.id, label: `${programLabel(t.programId ?? null)} • ${t.name}` }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.courseShortCode.trim()) {
      setError("Course Short Code is required");
      return;
    }

    setLoading(true);
    try {
      const res = isEditMode
        ? await CourseManagementService.updateCourse(courseId!, formData)
        : await CourseManagementService.createCourse(formData);

      if (res.success) {
        onSuccess();
      } else {
        setError(res.message || `Failed to ${isEditMode ? "update" : "create"} course`);
      }
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const headerTitle = `${viewOnly ? "View" : isEditMode ? "Edit" : "Create"} ${entityName}`;
  const headerSub = viewOnly
    ? `Read-only details for this ${entityName.toLowerCase()}`
    : isEditMode
      ? `Update the details of this ${entityName.toLowerCase()}`
      : `Fill in the details to add a new ${entityName.toLowerCase()}`;

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="cfp-offcanvas">
      <Offcanvas.Header closeButton className="cfp-header">
        <div className="cfp-header-inner">
          <span className="cfp-header-badge">
            {viewOnly ? <Icon.Eye size={22} /> : isEditMode ? <Icon.Edit2 size={20} /> : <Icon.Plus size={22} />}
          </span>
          <div>
            <h2 className="cfp-header-title">{headerTitle}</h2>
            <p className="cfp-header-sub">{headerSub}</p>
          </div>
        </div>
      </Offcanvas.Header>

      {fetching ? (
        <div className="cfp-loading">
          <Spinner role="status" animation="border" variant="primary" />
          <span>Loading {entityName.toLowerCase()} details…</span>
        </div>
      ) : (
        <Form onSubmit={handleSubmit} className="cfp-form d-flex flex-column h-100 overflow-hidden">
          <Offcanvas.Body className="cfp-body flex-grow-1 overflow-auto">
            {error && (
              <Alert variant="danger" dismissible onClose={() => setError(null)} className="cfp-alert py-2 px-3 mb-3 small">
                <Icon.AlertCircle size={15} className="me-2" />
                {error}
              </Alert>
            )}

            <fieldset disabled={viewOnly} className="cfp-fieldset">

              {/* Section: Basic Information */}
              <SectionCard icon={<Icon.Info size={16} />} title="Basic Information" accent="#6366f1">
                <div className="mb-3">
                  <Form.Label>
                    Name <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder={`${entityName} name`}
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    required
                  />
                </div>

                <div className="row g-3 mb-3 align-items-start">
                  <div className={isEditMode ? "col-8" : "col-12"}>
                    <Form.Label>
                      Short Code <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      placeholder={`${entityName.toUpperCase()}_CODE`}
                      className="w-100"
                      value={formData.courseShortCode}
                      onChange={(e) => handleChange("courseShortCode", e.target.value.toUpperCase())}
                      required
                    />
                  </div>
                  {isEditMode && (
                    <div className="col-4">
                      <Form.Label>Status</Form.Label>
                      <Form.Select
                        className="w-100"
                        value={formData.enableStatus ?? 1}
                        onChange={(e) => handleChange("enableStatus", Number(e.target.value))}
                      >
                        <option value={1}>Active</option>
                        <option value={0}>Inactive</option>
                      </Form.Select>
                    </div>
                  )}
                </div>

                <div className="row g-3">
                  <div className="col-4">
                    <Form.Label>Course Type</Form.Label>
                    <Form.Select
                      className="w-100"
                      value={formData.courseType ?? ""}
                      onChange={(e) => handleChange("courseType", e.target.value ? Number(e.target.value) : null)}
                    >
                      <option value="">-- None --</option>
                      {lookups.courseTypes.map((ct) => (
                        <option key={ct.id} value={ct.id}>{ct.name}</option>
                      ))}
                    </Form.Select>
                  </div>
                  {lookups.isProgramBased && (
                    <div className="col-4">
                      <Form.Label>Stream</Form.Label>
                      <Form.Select
                        className="w-100"
                        value={formData.streamId ?? ""}
                        onChange={(e) => handleChange("streamId", e.target.value ? Number(e.target.value) : null)}
                      >
                        <option value="">-- None --</option>
                        {lookups.streams.map((s) => (
                          <option key={s.id} value={s.id}>{s.name}</option>
                        ))}
                      </Form.Select>
                    </div>
                  )}
                  {lookups.isProgramBased && (
                    <div className="col-4">
                      <Form.Label>Is Elective</Form.Label>
                      <Form.Select
                        className="w-100"
                        value={formData.isElective === null ? "" : String(formData.isElective)}
                        onChange={(e) => handleChange("isElective", e.target.value === "" ? null : Number(e.target.value))}
                      >
                        <option value="">-- Not Set --</option>
                        <option value="1">Yes</option>
                        <option value="0">No</option>
                      </Form.Select>
                    </div>
                  )}
                </div>
              </SectionCard>

              {/* Section: Schedule & Capacity */}
              <SectionCard icon={<Icon.Calendar size={16} />} title="Schedule & Capacity" accent="#f59e0b">
                <div className="row g-3 mb-3">
                  <div className="col-4">
                    <Form.Label>Sessions</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.numberOfSessions ?? ""}
                      onChange={(e) => handleNumberChange("numberOfSessions", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>Max Students</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.maxStudents ?? ""}
                      onChange={(e) => handleNumberChange("maxStudents", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>Sort Order</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.sortOrder ?? ""}
                      onChange={(e) => handleNumberChange("sortOrder", e.target.value)}
                    />
                  </div>
                </div>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <Form.Label>Start Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.startDate ?? ""}
                      onChange={(e) => handleChange("startDate", e.target.value || null)}
                    />
                  </div>
                  <div className="col-6">
                    <Form.Label>End Date</Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.endDate ?? ""}
                      onChange={(e) => handleChange("endDate", e.target.value || null)}
                    />
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-4">
                    <Form.Label>Lectures</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.lectures ?? ""}
                      onChange={(e) => handleNumberChange("lectures", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>Tutorials</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.tutorials ?? ""}
                      onChange={(e) => handleNumberChange("tutorials", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>Practicals</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.practicals ?? ""}
                      onChange={(e) => handleNumberChange("practicals", e.target.value)}
                    />
                  </div>
                </div>
              </SectionCard>

              {/* Section: Credits & Fees (program-based mode only) */}
              {lookups.isProgramBased && (
              <SectionCard icon={<Icon.DollarSign size={16} />} title="Credits & Fees" accent="#10b981">
                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <Form.Label>Internal Credit</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.internalCredit ?? ""}
                      onChange={(e) => handleNumberChange("internalCredit", e.target.value)}
                    />
                  </div>
                  <div className="col-6">
                    <Form.Label>External Credit</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.externalCredit ?? ""}
                      onChange={(e) => handleNumberChange("externalCredit", e.target.value)}
                    />
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-4">
                    <Form.Label>List Price</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.listPrice ?? ""}
                      onChange={(e) => handleNumberChange("listPrice", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>Local Fee</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.localFee ?? ""}
                      onChange={(e) => handleNumberChange("localFee", e.target.value)}
                    />
                  </div>
                  <div className="col-4">
                    <Form.Label>International Fee</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.internationalFee ?? ""}
                      onChange={(e) => handleNumberChange("internationalFee", e.target.value)}
                    />
                  </div>
                </div>
              </SectionCard>
              )}

              {/* Section: Remarks */}
              <SectionCard icon={<Icon.MessageSquare size={16} />} title="Remarks" accent="#64748b">
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Optional remarks…"
                  value={formData.remarks ?? ""}
                  onChange={(e) => handleChange("remarks", e.target.value || null)}
                  style={{ resize: "vertical" }}
                />
              </SectionCard>

              {/* Section: Program & Term Mapping (program-based mode only) */}
              {lookups.isProgramBased && termGroupedOptions.length > 0 && (
                <SectionCard
                  icon={<Icon.Layers size={16} />}
                  title={`${label("program", "Program")} & ${labelPlural("term", "Term")}`}
                  accent="#8b5cf6"
                  extra={`${formData.termIds.length} ${viewOnly ? "mapped" : "selected"}`}
                >
                  {viewOnly ? (
                    selectedTermOptions.length === 0 ? (
                      <div className="cfp-empty-hint">
                        No {labelPlural("term", "Term").toLowerCase()} mapped.
                      </div>
                    ) : (
                      <div className="cfp-chips">
                        {selectedTermOptions.map((o) => (
                          <span key={o.value} className="cfp-chip">
                            <Icon.Layers size={12} />
                            {o.label}
                          </span>
                        ))}
                      </div>
                    )
                  ) : (
                    <>
                      <ReactSelect
                        isMulti
                        closeMenuOnSelect={false}
                        placeholder={`Select ${labelPlural("term", "Term").toLowerCase()} across ${labelPlural("program", "Program").toLowerCase()}…`}
                        options={termGroupedOptions}
                        value={selectedTermOptions}
                        onChange={(opts: any) =>
                          handleChange("termIds", (opts || []).map((o: any) => o.value))
                        }
                        styles={{
                          control: (base) => ({ ...base, minHeight: 40, borderRadius: 9, borderColor: "var(--border-strong)" }),
                          menu: (base) => ({ ...base, zIndex: 20 }),
                          groupHeading: (base) => ({
                            ...base,
                            fontSize: 11,
                            fontWeight: 700,
                            color: "var(--brand-accent)",
                            textTransform: "uppercase",
                          }),
                        }}
                      />
                      <div className="cfp-empty-hint" style={{ paddingBottom: 0 }}>
                        Pick {labelPlural("term", "Term").toLowerCase()} from one or more {labelPlural("program", "Program").toLowerCase()} — each selection is one {label("program", "Program").toLowerCase()}+{label("term", "Term").toLowerCase()} mapping.
                      </div>
                    </>
                  )}
                </SectionCard>
              )}

              {/* Section: School Assignments */}
              {lookups.schools.length > 0 && (
                <SectionCard
                  icon={<Icon.MapPin size={16} />}
                  title={labelPlural("school", "School")}
                  accent="#3b82f6"
                  extra={`${formData.schoolIds.length} ${viewOnly ? "mapped" : "selected"}`}
                >
                  {viewOnly ? (
                    formData.schoolIds.length === 0 ? (
                      <div className="cfp-empty-hint">
                        No {labelPlural("school", "School").toLowerCase()} mapped.
                      </div>
                    ) : (
                      <div className="cfp-chips">
                        {lookups.schools
                          .filter((s) => formData.schoolIds.includes(s.id))
                          .map((s) => (
                            <span key={s.id} className="cfp-chip">
                              <Icon.MapPin size={12} />
                              {s.name}
                            </span>
                          ))}
                      </div>
                    )
                  ) : (
                    <div className="cfp-checklist">
                      <Form.Check
                        type="checkbox"
                        id="school-select-all"
                        className="cfp-check-all"
                        label={allSchoolsSelected ? "Deselect all" : "Select all"}
                        checked={allSchoolsSelected}
                        onChange={toggleAllSchools}
                      />
                      {lookups.schools.map((school) => (
                        <Form.Check
                          key={school.id}
                          type="checkbox"
                          id={`school-${school.id}`}
                          label={school.name}
                          checked={formData.schoolIds.includes(school.id)}
                          onChange={() => toggleSchool(school.id)}
                        />
                      ))}
                    </div>
                  )}
                </SectionCard>
              )}

            </fieldset>
          </Offcanvas.Body>

          {/* Footer */}
          <div className="cfp-footer">
            <Button variant="secondary" onClick={onHide} disabled={loading}>
              {viewOnly ? "Close" : "Cancel"}
            </Button>
            {!viewOnly && (
              <Button variant="primary" type="submit" disabled={loading}>
                {loading ? (
                  <Spinner role="status" animation="border" size="sm" />
                ) : (
                  <>
                    <Icon.Save size={15} className="me-1" />
                    {isEditMode ? "Update" : "Create"}
                  </>
                )}
              </Button>
            )}
          </div>
        </Form>
      )}
    </Offcanvas>
  );
};

export default CourseFormPanel;
